import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  CircularProgress,
} from "@mui/material";
import { useParams, useNavigate, Link as RouterLink } from "react-router-dom";
import ArrowBack from "@mui/icons-material/ArrowBack";
import Pause from "@mui/icons-material/Pause";
import PlayArrow from "@mui/icons-material/PlayArrow";
import EventRepeat from "@mui/icons-material/EventRepeat";
import CheckCircle from "@mui/icons-material/CheckCircle";
import {
  useGetSubscriptionDetail,
  usePauseSubscription,
  useResumeSubscription,
} from "../../hooks/api/useSubscription";
import {
  getCompanyDetailPath,
  getPlanDetailPath,
  getBillingCycleDetailPath,
  getSubscriptionDetailPath,
} from "../../constants/routes";
import type { Subscription } from "../../Graphql/types/subscription";
import { ROUTE_SUBSCRIPTIONS } from "../../constants/routes";
import { responsiveHeading } from "../../styles/layoutStyles";
import ExtendSubscriptionModal from "./ExtendSubscriptionModal";
import AdminActivatePendingSubscriptionModal from "./AdminActivatePendingSubscriptionModal";
import { isPendingActivationStatus } from "./utils/subscriptionUtils";
import { formatSubscriptionStatusLabel } from "../common/adminEnumLabels";

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
}

function DetailItem({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <Box>
      <Typography
        variant="caption"
        color="text.secondary"
        display="block"
        sx={{ mb: 0.5 }}
      >
        {label}
      </Typography>
      <Typography
        variant="body1"
        sx={{
          fontFamily: mono ? "monospace" : undefined,
          fontSize: mono ? "0.875rem" : undefined,
        }}
      >
        {value || "—"}
      </Typography>
    </Box>
  );
}

function DetailLink({
  label,
  to,
  value,
}: {
  label: string;
  to: string;
  value: string;
}) {
  return (
    <Box>
      <Typography
        variant="caption"
        color="text.secondary"
        display="block"
        sx={{ mb: 0.5 }}
      >
        {label}
      </Typography>
      <Link
        component={RouterLink}
        to={to}
        sx={{
          fontFamily: "monospace",
          fontSize: "0.875rem",
          cursor: "pointer",
        }}
      >
        {value || "—"}
      </Link>
    </Box>
  );
}

const SubscriptionDetailView: React.FC = () => {
  const { subscriptionId } = useParams<{ subscriptionId: string }>();
  const navigate = useNavigate();
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [confirmPauseOpen, setConfirmPauseOpen] = useState(false);
  const [extendModalOpen, setExtendModalOpen] = useState(false);
  const [activateModalOpen, setActivateModalOpen] = useState(false);

  const getDetail = useGetSubscriptionDetail();
  const pauseSubscription = usePauseSubscription();
  const resumeSubscription = useResumeSubscription();

  useEffect(() => {
    if (!subscriptionId) return;
    getDetail
      .mutateAsync({ subscriptionId })
      .then((res) => {
        setSubscription(res.getSubscription?.subscription ?? null);
      })
      .catch(() => {});
  }, [subscriptionId]);

  useEffect(() => {
    if (getDetail.data?.getSubscription?.subscription) {
      setSubscription(getDetail.data.getSubscription.subscription);
    }
  }, [getDetail.data]);

  const handlePauseConfirm = async () => {
    if (!subscriptionId) return;
    try {
      const res = await pauseSubscription.mutateAsync(subscriptionId);
      if (res.pauseSubscription?.subscription) {
        setSubscription(res.pauseSubscription.subscription);
      }
      setConfirmPauseOpen(false);
    } catch {
      // Error handled by fetchGraphQL / setAlert
    }
  };

  const handleResumeClick = async () => {
    if (!subscriptionId) return;
    try {
      const res = await resumeSubscription.mutateAsync(subscriptionId);
      if (res.resumeSubscription?.subscription) {
        setSubscription(res.resumeSubscription.subscription);
      }
    } catch {
      // Error handled by fetchGraphQL / setAlert
    }
  };

  const handleExtendSuccess = () => {
    getDetail
      .mutateAsync({ subscriptionId: subscriptionId! })
      .then((res) => {
        setSubscription(res.getSubscription?.subscription ?? null);
      })
      .catch(() => {});
  };

  const isPausable = subscription?.status === "ACTIVE";
  const isResumable = subscription?.status === "PAUSED";
  const isExtendable =
    subscription?.status === "ACTIVE" || subscription?.status === "EXPIRED";
  const isActivatable =
    subscription != null && isPendingActivationStatus(subscription.status);

  if (getDetail.isPending && !subscription) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!subscription) {
    return (
      <Box sx={{ py: 3, px: 2 }}>
        <Typography color="text.secondary">Subscription not found.</Typography>
        <Button sx={{ mt: 2 }} onClick={() => navigate(ROUTE_SUBSCRIPTIONS)}>
          Back to list
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", boxSizing: "border-box", py: 3, px: 2 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 2,
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <IconButton
            aria-label="Back to subscriptions"
            onClick={() => navigate(ROUTE_SUBSCRIPTIONS)}
            size="small"
          >
            <ArrowBack />
          </IconButton>
          <Typography variant="h4" fontWeight="bold" sx={responsiveHeading}>
            Subscription details
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          {isActivatable && (
            <Button
              variant="contained"
              color="success"
              size="medium"
              startIcon={<CheckCircle />}
              onClick={() => setActivateModalOpen(true)}
            >
              Activate
            </Button>
          )}
          {isPausable && (
            <Button
              variant="outlined"
              color="warning"
              size="medium"
              startIcon={<Pause />}
              onClick={() => setConfirmPauseOpen(true)}
            >
              Pause
            </Button>
          )}
          {isResumable && (
            <Button
              variant="outlined"
              color="success"
              size="medium"
              startIcon={<PlayArrow />}
              onClick={handleResumeClick}
              disabled={resumeSubscription.isPending}
            >
              Resume
            </Button>
          )}
          {isExtendable && (
            <Button
              variant="outlined"
              color="primary"
              size="medium"
              startIcon={<EventRepeat />}
              onClick={() => setExtendModalOpen(true)}
            >
              Extend
            </Button>
          )}
        </Box>
      </Box>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight="600" gutterBottom>
          IDs (click to open)
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "1fr 1fr",
              md: "1fr 1fr 1fr",
            },
            gap: 2,
            mb: 3,
          }}
        >
          <DetailLink
            label="Subscription ID"
            to={getSubscriptionDetailPath(subscription.subscriptionId)}
            value={subscription.subscriptionId}
          />
          <DetailLink
            label="Company ID"
            to={getCompanyDetailPath(subscription.companyId)}
            value={subscription.companyId}
          />
          <DetailLink
            label="Plan ID"
            to={getPlanDetailPath(subscription.planId)}
            value={subscription.planId}
          />
          <DetailLink
            label="Billing cycle ID"
            to={getBillingCycleDetailPath(subscription.billingCycleId)}
            value={subscription.billingCycleId}
          />
        </Box>

        <Typography variant="subtitle1" fontWeight="600" gutterBottom>
          Details
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "1fr 1fr",
              md: "1fr 1fr 1fr",
            },
            gap: 2,
            mb: 3,
          }}
        >
          <DetailItem label="Plan name" value={subscription.planName} />
          <DetailItem
            label="Billing cycle code"
            value={subscription.billingCycleCode}
          />
          <DetailItem
            label="Price"
            value={
              subscription.priceAmount != null && subscription.priceCurrency
                ? `${subscription.priceAmount} ${subscription.priceCurrency}`
                : "—"
            }
          />
          <DetailItem
            label="Status"
            value={formatSubscriptionStatusLabel(subscription.status)}
          />
          <DetailItem
            label="Starts at"
            value={
              subscription.subscriptionStartsAt
                ? formatDate(subscription.subscriptionStartsAt)
                : "—"
            }
          />
          <DetailItem
            label="Ends at"
            value={
              subscription.subscriptionEndsAt
                ? formatDate(subscription.subscriptionEndsAt)
                : "—"
            }
          />
          <DetailItem
            label="Next billing at"
            value={
              subscription.nextBillingAt
                ? formatDate(subscription.nextBillingAt)
                : "—"
            }
          />
          <DetailItem
            label="Cancel at period end"
            value={subscription.cancelAtPeriodEnd ? "Yes" : "No"}
          />
          <DetailItem
            label="Created at"
            value={
              subscription.createdAt ? formatDate(subscription.createdAt) : "—"
            }
          />
          <DetailItem
            label="Updated at"
            value={
              subscription.updatedAt ? formatDate(subscription.updatedAt) : "—"
            }
          />
          {subscription.activatedAt && (
            <DetailItem
              label="Activated at"
              value={formatDate(subscription.activatedAt)}
            />
          )}
          {subscription.activatedBy && (
            <DetailItem
              label="Activated by"
              value={subscription.activatedBy}
              mono
            />
          )}
        </Box>
      </Paper>

      <Dialog
        open={confirmPauseOpen}
        onClose={() => setConfirmPauseOpen(false)}
      >
        <DialogTitle>Pause subscription</DialogTitle>
        <DialogContent>
          <Typography>
            This will set the subscription status to PAUSED only. End date and
            next billing date will remain unchanged. Are you sure?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmPauseOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handlePauseConfirm}
            disabled={pauseSubscription.isPending}
          >
            Pause
          </Button>
        </DialogActions>
      </Dialog>

      <ExtendSubscriptionModal
        open={extendModalOpen}
        onClose={() => setExtendModalOpen(false)}
        subscription={subscription}
        onSuccess={handleExtendSuccess}
      />

      <AdminActivatePendingSubscriptionModal
        open={activateModalOpen}
        onClose={() => setActivateModalOpen(false)}
        subscription={subscription}
        onSuccess={handleExtendSuccess}
      />
    </Box>
  );
};

export default SubscriptionDetailView;
